/**
 * Images pasted or dropped into a Doc upload as their own image frame:
 * beside the source frame on the old canvas, placed by the backend on the
 * wall. Used by the Doc's `uploadFile` and the workspace drop handlers.
 */
import {
  pasteToImageFrame,
  UnsupportedImageError,
  type PasteToImageFrameCtx,
  type PasteToImageFrameResult,
} from "@/lib/paste-to-image-frame";
import { workspaceViewAdapter } from "@/lib/workspace-view-adapter";
import { useNotificationStore } from "@/stores/notification-store";
import { useProjectStore } from "@/stores/project-store";
import { translate } from "@/i18n";
import { framesNoteMessages } from "@/i18n/messages/frames-note";

/**
 * Where images pasted into `sourceFrameId` upload to, or `null` outside an
 * open project.
 */
export function frameImageUploadTarget(
  sourceFrameId: string | undefined,
): PasteToImageFrameCtx | null {
  const { currentProject, currentSpace } = useProjectStore.getState();
  if (!currentProject || !currentSpace) return null;
  const [beside] =
    workspaceViewAdapter().framePositions({ kind: "beside", sourceFrameId }) ??
    [];
  return {
    projectId: currentProject.id,
    spaceId: currentSpace.id,
    ...beside,
    sourceFrameId,
  };
}

/**
 * Upload one image to its own frame. On failure a toast explains why
 * (unsupported format or upload error) and the result is `null`.
 */
export async function uploadFrameImage(
  file: File,
  target: PasteToImageFrameCtx,
): Promise<PasteToImageFrameResult | null> {
  try {
    return await pasteToImageFrame(file, target);
  } catch (err) {
    const isUnsupported = err instanceof UnsupportedImageError;
    useNotificationStore.getState().addSystemToast({
      id: `note-image-paste-fail-${Date.now()}`,
      level: "error",
      title: translate(
        framesNoteMessages,
        isUnsupported ? "imageUnsupportedTitle" : "imageUploadFailedTitle",
      ),
      description: isUnsupported
        ? translate(framesNoteMessages, "imageUnsupportedBody")
        : err instanceof Error
          ? err.message
          : translate(framesNoteMessages, "imageUploadFailedBody"),
    });
    return null;
  }
}
